import './fristen.css';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, tl, tn, tx } from '../../i18n';
import { fmtDate, fmtDayMonth, monthName, monthShort } from '../../lib/format';
import { eurC, inDays } from '../../lib/money';
import { NoteRow } from '../belege/Bell';
import { useNotifications, useShared } from '../belege/shared';
import type { Filing, FristenResponse, PeriodInfo } from './types';

// Teile der Übersicht zur Umsatzsteuer (wie in der Demo): nächste UStVA bzw. zuletzt übermittelte mit Zahllast,
// bald fällige Umsatzsteuer und übermittelte UStVA mit Protokoll. Übermittelt wird vom Steuerberater, die Kanzlei
// lädt das Protokoll hoch.

const RHYTHM = { M: tl('monatlich'), Q: tl('quartalsweise'), J: tl('jährlich'), B: tl('befreit') };

// Zeiträume vom Server in der Sprache der App: „Quartal 3 / 2026“, „September 2026“; Monate „Juli–September“
const shortOf = (p: PeriodInfo) => (p.kind === 'M' ? `${monthName(p.n)} ${p.year}` : tx(p.short));
const monthsOf = (p: PeriodInfo) => p.months.split('–').map(tx).join('–');

export const useFristen = (clientId: string) =>
  useShared<FristenResponse>(`/api/app/fristen/${clientId}`, ['fristen', 'clients']);

function useOpenProtocol() {
  const navigate = useNavigate();
  return (id: string) => navigate({ search: `?ustva=${id}` }, { state: { sheet: true } });
}

// Meldung „UStVA … übermittelt“, bis sie geöffnet ist (bei mehreren Firmen mit Firmennamen)
export function FristenNotices() {
  const notes = useNotifications();
  const items = (notes.data?.items ?? []).filter((n) => n.type === 'ustva_filed' && !n.read);
  if (!items.length) return null;
  return (
    <div className="notice" role="status">
      {items.map((n) => (
        <NoteRow key={n.id} item={n} />
      ))}
    </div>
  );
}

// „Zahllast 2.950,40 € laut Protokoll, fällig am 12.10.2026.“
export function amountText(f: Pick<Filing, 'amount' | 'due'>): ReactNode {
  if (f.amount === null) return t('Den Betrag finden Sie im Protokoll.');
  if (f.amount < 0) {
    return tn('Erstattung {amount} laut Protokoll. Das Finanzamt überweist den Betrag.', { amount: <b>{eurC(-f.amount)}</b> });
  }
  if (f.amount === 0) return t('Laut Protokoll ist nichts zu zahlen.');
  return tn('Zahllast {amount} laut Protokoll, fällig am {date}.', { amount: <b>{eurC(f.amount)}</b>, date: fmtDate(f.due) });
}

function DateBox({ date, title, sub }: { date: string; title: string; sub: string }) {
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return (
    <div className="due">
      <div className="date">
        <b>{d}</b>
        <small>{monthShort(m).toUpperCase()}</small>
      </div>
      <div className="tx">
        <b>{title}</b>
        <small>{sub}</small>
      </div>
    </div>
  );
}

// Karte „Nächste UStVA“ bzw., solange die Zahlung der zuletzt übermittelten noch ansteht, „UStVA · Übermittelt“
export function UstvaCard({ clientId }: { clientId: string }) {
  const res = useFristen(clientId);
  const open = useOpenProtocol();
  const d = res.data;
  if (!d || d.country !== 'DE') return null;
  const r = t(RHYTHM[d.rhythm]);
  const cur = d.filings.find((f) => f.due >= d.today);
  const next = d.next;

  if (cur) {
    const j = cur.kind === 'J';
    return (
      <div className="card ustva">
        <h3>
          {j ? t('USt-Erklärung') : t('UStVA')} <span>{r}</span>
        </h3>
        <div className="gl">
          {j
            ? t('Umsatzsteuer-Jahreserklärung ans Finanzamt · Zahllast = die Umsatzsteuer, die Sie zahlen')
            : t('Umsatzsteuer-Voranmeldung ans Finanzamt · Zahllast = die Umsatzsteuer, die Sie zahlen')}
        </div>
        <DateBox
          date={cur.due}
          title={tx(cur.title)}
          sub={
            cur.amount !== null && cur.amount > 0
              ? t('Übermittelt · Zahlung bis {date} · {when}', { date: fmtDate(cur.due), when: inDays(cur.due, d.today) })
              : t('Übermittelt am {date}', { date: fmtDate(cur.filedOn) })
          }
        />
        <div className="next">
          <span className="st ok">
            {cur.corrected
              ? t('Berichtigt am {date}', { date: fmtDate(cur.filedOn) })
              : t('Übermittelt am {date}', { date: fmtDate(cur.filedOn) })}
          </span>{' '}
          {amountText(cur)}
        </div>
        <div className="bwa-row">
          <button type="button" className="btn2" onClick={() => open(cur.id)}>
            {t('Protokoll ansehen')}
          </button>
        </div>
        <div className="next">
          {next
            ? t('Nächste Abgabe: {title} bis {date} · {when}', {
                title: tx(next.title),
                date: fmtDate(next.deadline),
                when: inDays(next.deadline, d.today),
              })
            : t('Danach fallen keine Voranmeldungen mehr an.')}
        </div>
      </div>
    );
  }

  if (!next) {
    return (
      <div className="card ustva">
        <h3>
          {t('Umsatzsteuer')} <span>{r}</span>
        </h3>
        <div className="next first">
          {t('Sie sind von der Umsatzsteuer befreit, z. B. als Kleinunternehmer. Es fallen keine Voranmeldungen an.')}
        </div>
      </div>
    );
  }

  const j = next.kind === 'J';
  const after = d.after;
  return (
    <div className="card ustva">
      <h3>
        {j ? t('Nächste USt-Erklärung') : t('Nächste UStVA')} <span>{r}</span>
      </h3>
      <div className="gl">
        {j ? t('Umsatzsteuer-Jahreserklärung ans Finanzamt') : t('Umsatzsteuer-Voranmeldung ans Finanzamt')}
      </div>
      <DateBox
        date={next.deadline}
        title={tx(next.title)}
        sub={t('Abgabe bis {date} · {when}', { date: fmtDate(next.deadline), when: inDays(next.deadline, d.today) })}
      />
      <div className="next">
        {j
          ? t('Keine Voranmeldungen im Jahr, nur die Jahreserklärung.')
          : after
            ? t('Danach: {period} bis {date}', { period: shortOf(after), date: fmtDate(after.deadline) })
            : t('Danach fallen keine Voranmeldungen mehr an.')}
      </div>
      <div className="next">
        {j ? t('Ihr Steuerberater übermittelt die Erklärung für Sie.') : t('Ihr Steuerberater übermittelt die UStVA für Sie.')}{' '}
        {next.over && !j
          ? next.asap
            ? t('Ihre Belege für {months} bitte so bald wie möglich hochladen.', { months: monthsOf(next) })
            : t('Ihre Belege für {months} bitte bis {date} hochladen.', {
                months: monthsOf(next),
                date: fmtDayMonth(next.belegeBy),
              })
          : t('Sie laden nur Ihre Belege hoch.')}
      </div>
    </div>
  );
}

function More({ n, all, toggle }: { n: number; all: boolean; toggle(): void }) {
  if (n < 2) return null;
  return (
    <button type="button" className="more" aria-expanded={all} onClick={toggle}>
      {all ? t('Weniger anzeigen') : t('Alle anzeigen ({n})', { n })}
      <span aria-hidden="true">{all ? '▴' : '▾'}</span>
    </button>
  );
}

// Karte „Bald fällige Steuern“: Umsatzsteuer der nächsten 45 Tage (Betrag laut Protokoll oder erst nach der Abgabe)
export function TaxesCard({ clientId }: { clientId: string }) {
  const res = useFristen(clientId);
  const open = useOpenProtocol();
  const [all, setAll] = useState(false);
  const d = res.data;
  if (!d?.taxes.length) return null;
  const rows = all ? d.taxes : d.taxes.slice(0, 1);
  return (
    <div className="card ustva">
      <h3>{t('Bald fällige Steuern')}</h3>
      <div className="lines">
        {rows.map((x) => {
          const sub = (
            <>
              {tn('fällig {date} · {when}', {
                date: fmtDate(x.due),
                when: <span className="soon">{inDays(x.due, d.today)}</span>,
              })}
              {x.filingId
                ? x.amount !== null
                  ? ` · ${t('laut Protokoll')}`
                  : ` · ${t('übermittelt, Betrag im Protokoll')}`
                : ` · ${t('Betrag nach der Abgabe')}`}
            </>
          );
          return x.filingId ? (
            <button key={`${x.title}-${x.due}`} type="button" className="ln lnb" onClick={() => open(x.filingId!)}>
              <span>
                {tx(x.title)}
                <small>
                  {sub} · {t('Protokoll ansehen')}
                </small>
              </span>
              <span className="amt">
                {x.amount !== null ? eurC(x.amount) : '–'}{' '}
                <span className="chev" aria-hidden="true">
                  ›
                </span>
              </span>
            </button>
          ) : (
            <div key={`${x.title}-${x.due}`} className="ln">
              <span>
                {tx(x.title)}
                <small>{sub}</small>
              </span>
              <span className="amt">–</span>
            </div>
          );
        })}
      </div>
      <More n={d.taxes.length} all={all} toggle={() => setAll((x) => !x)} />
      {d.taxes.some((x) => x.filingId && x.amount !== null) && (
        <div className="src">{t('Beträge „laut Protokoll“ stammen aus dem Übertragungsprotokoll der UStVA.')}</div>
      )}
    </div>
  );
}

// Karte „Übermittelte UStVA“: neueste zuerst, jede mit Protokoll
export function FiledCard({ clientId }: { clientId: string }) {
  const res = useFristen(clientId);
  const open = useOpenProtocol();
  const [all, setAll] = useState(false);
  const d = res.data;
  if (!d?.filings.length) return null;
  const rows = all ? d.filings : d.filings.slice(0, 1);
  return (
    <div className="card ustva">
      <h3>{t('Übermittelte UStVA')}</h3>
      <div className="lines">
        {rows.map((f) => (
          <button key={f.id} type="button" className="ln lnb" onClick={() => open(f.id)}>
            <span>
              {tx(f.title)}
              <small>
                {f.corrected
                  ? t('berichtigt am {date}', { date: fmtDate(f.filedOn) })
                  : t('übermittelt am {date}', { date: fmtDate(f.filedOn) })}
                {f.amount !== null && f.amount < 0
                  ? ` · ${t('Erstattung')}`
                  : f.amount
                    ? ` · ${f.due >= d.today ? t('Zahlung bis {date}', { date: fmtDate(f.due) }) : t('fällig am {date}', { date: fmtDate(f.due) })}`
                    : ''}{' '}
                · {t('Protokoll ansehen')}
              </small>
            </span>
            <span className="amt">
              {f.amount !== null ? eurC(Math.abs(f.amount)) : '–'}{' '}
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </span>
          </button>
        ))}
      </div>
      <More n={d.filings.length} all={all} toggle={() => setAll((x) => !x)} />
    </div>
  );
}
